import React, { useState } from "react";
import dayjs from "dayjs";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { Box, Button, Grid, IconButton } from "@mui/material";
import ErrorHelper from "../../../../../../components/ErrorWrapper";
import Input from "../../../../../../components/Input";
import Text from "../../../../../../components/Text";
import SignatureInput from "../../../../../../components/signiture/SignatureInput";
import { useSignatureController } from "../../../../../../components/signiture/use-signature-controller";
import { ProviderGroupPracticeInfo } from "@/queries/use-get-provider-group-practice-info";
import { getAddressFormat } from "@/utils/utils";

interface PrivacyPracticesFormProps {
  values: any;
  setFieldValue: (field: string, value: any) => void;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  touched: any;
  errors: any;
  viewOnly?: boolean;
  practiceInfo?: ProviderGroupPracticeInfo;
}

const PrivacyPracticesForm = ({
  values,
  setFieldValue,
  handleChange,
  handleBlur,
  touched: _touched,
  errors: _errors,
  viewOnly,
  practiceInfo,
}: PrivacyPracticesFormProps) => {
  const [_k, setK] = useState<boolean>(true);
  const { canvasRef, signPad, isSignEmpty } = useSignatureController(
    _k,
    values?.privacyPractices?.sign
  );
  const effectiveDate = dayjs().format("MMMM D, YYYY");
  const practiceName = practiceInfo?.name;
  const practiceAddress = getAddressFormat(practiceInfo?.address);
  const practicePhone = practiceInfo?.phone;

  return (
    <Box>
      <Text variant="subtitle1" fontWeight="500" mb={1}>
        Preview: Notice of Privacy Practices
      </Text>

      {/* Scrollable Legal Content Viewport */}
      <Box
        sx={{
          border: "1px solid #c0c0c0",
          borderRadius: "4px",
          p: 2,
          mb: 3,
          backgroundColor: "#ffffff",
          fontFamily: "sans-serif",
          fontSize: "0.9rem",
          lineHeight: "1.5",
          color: "#333333",
        }}
      >
        <p style={{ textAlign: "center", fontWeight: "bold" }}>
          THIS NOTICE DESCRIBES HOW MEDICAL INFORMATION ABOUT YOU MAY BE USED
          AND DISCLOSED AND HOW YOU CAN GET ACCESS TO THIS INFORMATION. PLEASE
          REVIEW IT CAREFULLY.
        </p>

        <p>
          {practiceName} (the “Practice”) is committed to protecting your
          privacy. The Practice is required by federal law to maintain the
          privacy of Protected Health Information (“PHI”), which is information
          that identifies or could be used to identify you. The Practice is
          required to provide you with this Notice of Privacy Practices (this
          “Notice”), which explains the Practice's legal duties and privacy
          practices and your rights regarding PHI that we collect and maintain.
        </p>

        <h4 style={{ margin: "16px 0 8px 0" }}>YOUR RIGHTS</h4>
        <p>
          Your rights regarding PHI are explained below. To exercise these
          rights, please submit a written request to the Practice at the address
          noted below.
        </p>

        <strong>To inspect and copy PHI.</strong>
        <ul>
          <li>
            You can ask for an electronic or paper copy of PHI. The Practice may
            charge you a reasonable fee.
          </li>
          <li>
            The Practice may deny your request if it believes the disclosure
            will endanger your life or another person's life. You may have a
            right to have this decision reviewed.
          </li>
        </ul>

        <strong>To amend PHI.</strong>
        <ul>
          <li>
            You can ask to correct PHI you believe is incorrect or incomplete.
            The Practice may require you to make your request in writing and
            provide a reason for the request.
          </li>
          <li>
            The Practice may deny your request. The Practice will send a written
            explanation for the denial and allow you to submit a written
            statement of disagreement.
          </li>
        </ul>

        <strong>To request confidential communications.</strong>
        <ul>
          <li>
            You can ask the Practice to contact you in a specific way. The
            Practice will say “yes” to all reasonable requests.
          </li>
        </ul>

        <strong>To limit what is used or shared.</strong>
        <ul>
          <li>
            You can ask the Practice not to use or share PHI for treatment,
            payment, or business operations. The Practice is not required to
            agree if it would affect your care.
          </li>
          <li>
            If you pay for a service or health care item out-of-pocket in full,
            you can ask the Practice not to share PHI with your health insurer.
          </li>
          <li>
            You can ask for the Practice not to share your PHI with family
            members or friends by stating the specific restriction requested and
            to whom you want the restriction to apply.
          </li>
        </ul>

        <strong>
          To obtain a list of those with whom your PHI has been shared.
        </strong>
        <ul>
          <li>
            You can ask for a list, called an accounting, of the times your
            health information has been shared. You can receive one accounting
            every 12 months at no charge, but you may be charged a reasonable
            fee if you ask for one more frequently.
          </li>
        </ul>

        <strong>To receive a copy of this Notice.</strong>
        <ul>
          <li>
            You can ask for a paper copy of this Notice, even if you agreed to
            receive the Notice electronically.
          </li>
        </ul>

        <strong>To choose someone to act for you.</strong>
        <ul>
          <li>
            If you have given someone medical power of attorney or if someone is
            your legal guardian, that person can exercise your rights.
          </li>
        </ul>

        <strong>
          To file a complaint if you feel your rights are violated.
        </strong>
        <ul>
          <li>You can file a complaint by contacting the Practice.</li>
          <li>
            You can file a complaint with the U.S. Department of Health and
            Human Services Office for Civil Rights by sending a letter to 200
            Independence Avenue, S.W., Washington, D.C. 20201, calling
            1-877-696-6775, or visiting
            www.hhs.gov/ocr/privacy/hipaa/complaints/.
          </li>
          <li>
            The Practice will not retaliate against you for filing a complaint.
          </li>
        </ul>

        <strong>To opt out of receiving fundraising communications.</strong>
        <ul>
          <li>
            The Practice may contact you for fundraising efforts, but you can
            ask not to be contacted again.
          </li>
        </ul>

        <p>
          To file a complaint or exercise your rights, contact the practice
          using the information below:
          {practiceName && (
            <>
              <br />
              {practiceName}
            </>
          )}
          {practiceAddress && (
            <>
              <br />
              {practiceAddress}
            </>
          )}
          {practicePhone && (
            <>
              <br />
              {practicePhone}
            </>
          )}
        </p>

        <h4 style={{ margin: "16px 0 8px 0" }}>OUR USES AND DISCLOSURES</h4>

        <strong>1. Routine Uses and Disclosures of PHI</strong>
        <p>
          The Practice is permitted under federal law to use and disclose PHI,
          without your written authorization, for certain routine uses and
          disclosures, such as those made for treatment, payment, and the
          operation of our business. The Practice typically uses or shares your
          health information in the following ways:
        </p>
        <ul>
          <li>
            <strong>To treat you:</strong> The Practice can use and share PHI
            with other professionals who are treating you. (Example: Your
            primary care doctor asks about your mental health treatment.)
          </li>
          <li>
            <strong>To run the health care operations:</strong> The Practice can
            use and share PHI to run the business, improve your care, and
            contact you. (Example: The Practice uses PHI to send you appointment
            reminders if you choose.)
          </li>
          <li>
            <strong>To bill for your services:</strong> The Practice can use and
            share PHI to bill and get payment from health plans or other
            entities. (Example: The Practice gives PHI to your health insurance
            plan so it will pay for your services.)
          </li>
        </ul>

        <strong>
          2. Uses and Disclosures of PHI That May Be Made Without Your
          Authorization or Opportunity to Object
        </strong>
        <p>
          The Practice may use or disclose PHI without your authorization or an
          opportunity for you to object, including:
        </p>
        <ul>
          <li>
            <strong>To help with public health and safety issues:</strong>{" "}
            Preventing the spread of disease, assisting in product recalls,
            reporting adverse medication reactions, responding to Health
            Oversight audits/inspections, reporting abuse or neglect, or
            mitigating serious imminent threats.
          </li>
          <li>
            <strong>
              Required by the Secretary of Health and Human Services:
            </strong>{" "}
            Disclosing your PHI to the Secretary of Health and Human Services to
            investigate or determine compliance.
          </li>
          <li>
            <strong>
              To comply with law, law enforcement, or other government requests:
            </strong>{" "}
            Serving judicial proceedings, subpoenas, specialized military
            functions, workers' compensation support, or national security
            clearances.
          </li>
          <li>
            <strong>To comply with other requests:</strong> Interacting with
            coroners/funeral directors, approved institutional research boards,
            or authorized business associates.
          </li>
        </ul>

        <strong>
          3. Uses and Disclosures of PHI That May Be Made With Your
          Authorization or Opportunity to Object
        </strong>
        <p>
          Unless you object, the Practice may disclose PHI to your family,
          friends, or others directly involved in your care if it aligns with
          your best interest during an incapacity.
        </p>

        <strong>
          4. Uses and Disclosures of PHI Based Upon Your Written Authorization
        </strong>
        <p>
          The Practice must obtain explicit written authorization for marketing
          initiatives, the sale of PHI, or releasing psychotherapy records.
        </p>

        <strong>
          5. Use and Disclosure of Substance Use Disorder Records Subject to 42
          CFR Part 2:
        </strong>
        <p>
          (A) Substance use disorder (“SUD”) records are protected under 42
          C.F.R. Part 2, requiring explicit consent rules unless resolving acute
          medical emergencies, child abuse reporting, or dealing with crimes
          committed on program premises.
        </p>
        <p>
          (B) SUD records are strictly protected from structural usage in civil,
          criminal, or legislative proceedings against you unless matching
          explicit written consent rules or specific court orders accompanied by
          targeted subpoenas.
        </p>

        <h4 style={{ margin: "16px 0 8px 0" }}>OUR RESPONSIBILITIES</h4>
        <ul>
          <li>
            The Practice is required by law to maintain the privacy and security
            of PHI.
          </li>
          <li>
            The Practice is required to abide by the terms of this Notice
            currently in effect. Where more stringent state or federal law
            governs PHI, the Practice will abide by the more stringent law.
          </li>
          <li>
            The Practice reserves the right to amend this Notice. Should changes
            occur, revised variants will be made available at the Practice.
          </li>
          <li>
            The Practice will inform you if PHI is compromised in a breach.
          </li>
        </ul>

        <p>
          <em>This Notice is effective on {effectiveDate}.</em>
        </p>
      </Box>

      {/* Signature Acknowledgment Blocks */}
      <Box>
        <Grid container spacing={2}>
          <Grid
            size={{
              xs: 12,
              sm: 6,
            }}
          >
            <Input
              label="Signed By:"
              textFieldProps={{
                name: "privacyPractices.signedByName",
                value: values?.privacyPractices?.signedByName || "",
                onChange: handleChange,
                onBlur: handleBlur,
                placeholder: "Enter Your Legal Name",
                disabled: viewOnly,
              }}
            />
          </Grid>
          <Grid size={12}>
            {viewOnly || values?.privacyPractices?.sign ? (
              <ErrorHelper label="Signature">
                <Box
                  style={{
                    position: "relative",
                    borderWidth: 1,
                    borderRadius: 5,
                    borderStyle: "solid",
                    width: "368px",
                    height: "100%",
                  }}
                >
                  <img
                    src={values?.privacyPractices?.sign}
                    alt="Notice of Privacy Practices signature"
                    style={{ height: "150px", width: "360px" }}
                  />

                  {!viewOnly && (
                    <IconButton
                      color="error"
                      onClick={() => {
                        setK((prev) => !prev);
                        signPad?.clear();
                        setFieldValue("privacyPractices.sign", undefined);
                      }}
                      sx={{
                        position: "absolute",
                        top: -8,
                        right: -20,
                        height: "10px",
                      }}
                    >
                      <HighlightOffIcon color="error" fontSize="medium" />
                    </IconButton>
                  )}
                </Box>
              </ErrorHelper>
            ) : (
              <Box
                onMouseLeave={() => {
                  if (!isSignEmpty() && signPad?.toDataURL()) {
                    setFieldValue(
                      "privacyPractices.sign",
                      signPad?.toDataURL()
                    );
                  }
                }}
                sx={{
                  width: "368px",
                  position: "relative",
                }}
              >
                <SignatureInput
                  label="Signature"
                  canvasRef={canvasRef}
                  w="368px"
                  h="100px"
                />

                <Button
                  variant="contained"
                  sx={{
                    position: "absolute",
                    bottom: 10,
                    left: "300px",
                    height: "24px",
                  }}
                  onClick={() => {
                    setK((prev) => !prev);
                    signPad?.clear();
                  }}
                >
                  clear
                </Button>
              </Box>
            )}
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default PrivacyPracticesForm;
